import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, ShieldCheck } from "@phosphor-icons/react";
import { api, fileUrl, serviceImage } from "../../lib/api";
import { useContent, pick } from "../../lib/content";
import { useSite } from "../../lib/site";
import { useServices } from "../../lib/services";
import { getBootstrap } from "../../lib/bootstrap";
import SEO from "../../components/SEO";
import CTABand from "../../components/public/CTABand";
import VideoEmbed from "../../components/public/VideoEmbed";

export default function ServiceDetail() {
  const c = useContent();
  const site = useSite();
  const allServices = useServices();
  const { slug } = useParams();
  // Initialize from bundled services list (instant render), then refresh
  // from the live API when available.
  const [svc, setSvc] = useState(() => allServices.find((s) => s.slug === slug) || null);
  const [pairs, setPairs] = useState(() => {
    const boot = getBootstrap("before_after");
    if (Array.isArray(boot)) {
      return boot.filter((p) => p.service_slug === slug);
    }
    return [];
  });
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setSvc(allServices.find((s) => s.slug === slug) || null);
  }, [allServices, slug]);

  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => setSvc(r.data)).catch((e) => {
      if (e?.response?.status === 404 && !svc) setNotFound(true);
    });
    api.get(`/before-after?service_slug=${slug}`).then((r) => {
      if (Array.isArray(r.data)) setPairs(r.data);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (notFound) {
    return (
      <div className="container-max py-32" data-testid="service-not-found">
        <h1 className="h-section">Service not found</h1>
        <Link to="/services" className="btn-outline mt-6">Back to services</Link>
      </div>
    );
  }
  if (!svc) return <div className="container-max py-32 text-atc-muted">Loading…</div>;

  const warrantyBody = pick(c, "service_detail.warranty_body_template", "Every {service} project is backed by our lifetime warranty on labor. Quality that lasts — guaranteed.").replace(/\{service\}/g, svc.title.toLowerCase());

  return (
    <div data-testid="service-detail-page">
      <SEO
        path={`/services/${svc.slug}`}
        title={`${svc.title} in ${site?.city || "Loganville, PA"}`}
        description={svc.short_description}
        image={svc.image_path ? fileUrl(svc.image_path) : undefined}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": svc.title,
          "description": svc.short_description,
          "provider": { "@id": `${(site?.site_url || "").replace(/\/$/, "")}/#business` },
          "areaServed": (site?.service_areas || []).map((a) => ({ "@type": "Place", "name": a })),
          "hasOfferCatalog": svc.features?.length > 0 ? {
            "@type": "OfferCatalog",
            "name": `${svc.title} — What's Included`,
            "itemListElement": svc.features.map((f) => ({ "@type": "Offer", "name": f })),
          } : undefined,
        }}
      />
      <section className="relative bg-atc-black text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={serviceImage(svc)} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-atc-black via-atc-black/70 to-transparent" />
        </div>
        <div className="container-max relative py-28 md:py-32">
          <Link to="/services" className="inline-flex items-center gap-2 text-white/60 hover:text-white font-heading uppercase tracking-widest text-xs mb-8" data-testid="back-to-services">
            <ArrowLeft size={14} weight="bold" /> {pick(c, "service_detail.back_label", "All Services")}
          </Link>
          <div className="overline mb-4 text-atc-red">ATC · Service</div>
          <h1 className="h-display max-w-4xl">{svc.title}</h1>
          <p className="mt-6 text-white/80 text-lg max-w-2xl">{svc.short_description}</p>
        </div>
      </section>

      <section className="section-pad bg-atc-paper" data-testid="service-intro">
        <div className="container-max grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="overline mb-4">{pick(c, "service_detail.overview_overline", "Overview")}</div>
            <h2 className="h-section">{pick(c, "service_detail.overview_title_line_1", "Built right.")}<br/>{pick(c, "service_detail.overview_title_line_2", "Warrantied for life.")}</h2>
            <div className="rule-red mt-6" />
          </div>
          <div className="md:col-span-8">
            <p className="text-lg leading-relaxed text-atc-charcoal whitespace-pre-line">{svc.long_description || svc.short_description}</p>
          </div>
        </div>
      </section>

      {svc.features?.length > 0 && (
        <section className="section-pad bg-white border-y-2 border-atc-black" data-testid="service-features">
          <div className="container-max">
            <div className="overline mb-4">{pick(c, "service_detail.features_overline", "What's included")}</div>
            <h2 className="h-section mb-10">{pick(c, "service_detail.features_title", "The full scope.")}</h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-0 border-2 border-atc-black">
              {svc.features.map((f, i) => {
                const lastRowMd = i >= svc.features.length - (svc.features.length % 2 || 2);
                return (
                  <li
                    key={i}
                    className={`flex items-start gap-3 p-5 bg-white border-atc-black
                      ${i !== svc.features.length - 1 ? "border-b-2" : ""}
                      ${i % 2 === 0 ? "md:border-r-2" : ""}
                      ${!lastRowMd ? "md:border-b-2" : "md:border-b-0"}`}
                  >
                    <Check size={22} weight="bold" className="text-atc-red shrink-0 mt-0.5" />
                    <span className="text-atc-charcoal">{f}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      )}

      {(svc.video_urls?.length || 0) > 0 && (
        <section className="section-pad bg-white border-y-2 border-atc-black" data-testid="service-videos">
          <div className="container-max">
            <div className="mb-10">
              <div className="overline mb-4">Watch</div>
              <h2 className="h-section">See the<br />work in action.</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {svc.video_urls.map((url, i) => (
                <VideoEmbed key={i} url={url} title={svc.title} />
              ))}
            </div>
          </div>
        </section>
      )}

      {pairs.length > 0 && (
        <section className="section-pad bg-atc-paper" data-testid="before-after">
          <div className="container-max">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div>
                <div className="overline mb-4">{pick(c, "service_detail.ba_overline", "Recent projects")}</div>
                <h2 className="h-section">{pick(c, "service_detail.ba_title_line_1", "Before")}<br/>{pick(c, "service_detail.ba_title_line_2", "& after.")}</h2>
              </div>
              <p className="md:max-w-md text-atc-muted text-lg">Real {svc.title.toLowerCase()} transformations from across Southern York County.</p>
            </div>
            <div className="space-y-12">
              {pairs.map((p, idx) => (
                <div key={p.id} className="grid grid-cols-1 md:grid-cols-2 gap-px bg-atc-black border-2 border-atc-black" data-testid={`ba-pair-${idx}`}>
                  <div className="relative aspect-[4/3] bg-atc-black overflow-hidden group">
                    <img src={fileUrl(p.before_image)} alt="Before" className="w-full h-full object-cover grayscale group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-4 left-4 bg-atc-black text-white px-3 py-1.5 font-heading uppercase tracking-widest text-xs font-bold">Before</div>
                  </div>
                  <div className="relative aspect-[4/3] bg-atc-black overflow-hidden group">
                    <img src={fileUrl(p.after_image)} alt="After" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-4 left-4 bg-atc-red text-white px-3 py-1.5 font-heading uppercase tracking-widest text-xs font-bold">After</div>
                  </div>
                  {(p.title || p.caption) && (
                    <div className="md:col-span-2 bg-white p-6 border-t-2 border-atc-black">
                      {p.title && <h3 className="font-heading uppercase text-xl text-atc-black">{p.title}</h3>}
                      {p.caption && <p className="text-atc-muted mt-2">{p.caption}</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-atc-red text-white py-16 relative overflow-hidden" data-testid="warranty-band">
        <div className="absolute inset-0 bg-blueprint-grid-dark bg-[size:3rem_3rem] opacity-20" />
        <div className="container-max relative flex flex-col md:flex-row items-center gap-6">
          <ShieldCheck size={64} weight="bold" />
          <div>
            <div className="overline text-white/80">{pick(c, "service_detail.warranty_overline", "Our promise")}</div>
            <h2 className="font-display text-3xl md:text-4xl uppercase leading-none mt-1">{pick(c, "service_detail.warranty_title", "Lifetime warranty on labor")}</h2>
            <p className="mt-2 text-white/90 max-w-2xl">{warrantyBody}</p>
          </div>
        </div>
      </section>

      <CTABand phone={site?.phone} />
    </div>
  );
}
